import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toDataSourceRequestString } from "@progress/kendo-data-query";
import RegisterApi from "../api/registerApi";
import moment from "moment";

const REGISTER_HISTORY_KEY = "registerHistory";

const formatDates = (data) => {
  (data || []).forEach((item) => {
    item.dteSignIn =
      item.dteSignIn == null
        ? null
        : moment.utc(item.dteSignIn).local().format("DD-MMM-YYYY HH:mm");
    item.dteSignOut =
      item.dteSignOut == null
        ? null
        : moment.utc(item.dteSignOut).local().format("DD-MMM-YYYY HH:mm");
    item.creationDate =
      item.creationDate == null
        ? null
        : moment.utc(item.creationDate).local().format("DD-MMM-YYYY HH:mm");
  });
  return data;
};

/**
 * Query: Fetch Register History in a given date range.
 * Cached by (dateFrom, dateTo). Changing either triggers a refetch.
 */
export const useRegisterHistory = (dateFrom, dateTo) => {
  return useQuery({
    queryKey: [
      REGISTER_HISTORY_KEY,
      dateFrom ? dateFrom.toISOString() : null,
      dateTo ? dateTo.toISOString() : null,
    ],
    queryFn: async () => {
      const filters = [];
      if (dateFrom) {
        // Normalize to midnight (DatePicker is date-only; time irrelevant)
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        filters.push({ field: "dteSignIn", operator: "gte", value: fromDate });
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        filters.push({ field: "dteSignIn", operator: "lte", value: toDate });
      }
      const state = {
        skip: 0,
        take: 0,
        ...(filters.length ? { filter: { logic: "and", filters } } : {}),
      };
      const queryStr = toDataSourceRequestString(state);
      const response = await RegisterApi.getRegisterHistory(queryStr);
      const payload = response.data || {};
      return {
        data: formatDates(payload.data || []),
        total: payload.total || 0,
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes fresh — no refetch on remount within window
    gcTime: 1000 * 60 * 10, // 10 minutes cache retention
  });
};

/**
 * Returns a function that invalidates all registerHistory queries,
 * triggering a background refetch wherever they're used.
 */
export const useInvalidateRegisterHistory = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [REGISTER_HISTORY_KEY] });
};
